import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle2, XCircle, AlertCircle, Clock, Loader2,
  ChevronDown, ChevronUp, Copy, ExternalLink, RefreshCw,
  Wifi, WifiOff, ShieldCheck, X, ArrowRight,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import api from "../../lib/api";

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS = {
  connected: {
    label: "Conectado",
    color: "text-[#25D366]",
    bg: "bg-[#25D366]/15 border-[#25D366]/30",
    dot: "bg-[#25D366]",
    Icon: CheckCircle2,
  },
  connected_mock: {
    label: "Conectado (dev)",
    color: "text-blue-400",
    bg: "bg-blue-400/15 border-blue-400/30",
    dot: "bg-blue-400",
    Icon: CheckCircle2,
  },
  disconnected: {
    label: "Desconectado",
    color: "text-slate-400",
    bg: "bg-slate-700/30 border-slate-700/50",
    dot: "bg-slate-500",
    Icon: WifiOff,
  },
  connecting: {
    label: "Conectando…",
    color: "text-yellow-400",
    bg: "bg-yellow-400/15 border-yellow-400/30",
    dot: "bg-yellow-400 animate-pulse",
    Icon: Loader2,
  },
  error: {
    label: "Erro de conexão",
    color: "text-red-400",
    bg: "bg-red-400/15 border-red-400/30",
    dot: "bg-red-400",
    Icon: XCircle,
  },
  webhook_pending: {
    label: "Webhook pendente",
    color: "text-amber-400",
    bg: "bg-amber-400/15 border-amber-400/30",
    dot: "bg-amber-400 animate-pulse",
    Icon: AlertCircle,
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS[status] ?? STATUS.disconnected;
  const { Icon } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── Log viewer ────────────────────────────────────────────────────────────────

function IntegrationLogs({ logs = [] }) {
  if (!logs.length) {
    return (
      <p className="text-[11px] text-slate-600 italic">Nenhum log disponível ainda.</p>
    );
  }
  const levelColor = { info: "text-slate-400", error: "text-red-400", warn: "text-amber-400" };
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "—";
    }
  };
  return (
    <div className="space-y-1 max-h-36 overflow-y-auto pr-1 custom-scrollbar">
      {[...logs].reverse().map((log, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-[10px] text-slate-600 font-mono shrink-0 mt-0.5">
            [{fmt(log.timestamp)}]
          </span>
          <span className={`text-[11px] leading-tight ${levelColor[log.level] ?? "text-slate-400"}`}>
            {log.message}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Connection modal ──────────────────────────────────────────────────────────

function MetaConnectionModal({ open, onClose, provider, providerLabel, providerIcon: ProviderIcon, onConnect }) {
  const [step, setStep] = useState(1); // 1 = info, 2 = connecting
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setBusy(false); };

  const handleClose = () => { reset(); onClose(); };

  const handleConnect = async () => {
    setBusy(true);
    setStep(2);
    try {
      await onConnect();
    } catch {
      setStep(1);
    }
    setBusy(false);
  };

  if (!open) return null;

  const steps = {
    whatsapp: [
      "Escolha seu Business Manager da Meta",
      "Selecione sua conta WhatsApp Business (WABA)",
      "Escolha o número de telefone oficial",
    ],
    instagram: [
      "Faça login na sua conta Meta",
      "Selecione a Página do Facebook vinculada",
      "Autorize acesso ao Instagram profissional",
    ],
    facebook: [
      "Faça login na sua conta Meta",
      "Selecione a Página do Facebook",
      "Autorize o Cliver CRM a gerenciar mensagens",
    ],
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
              <ProviderIcon className="w-5 h-5 text-slate-300" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                Conectar {providerLabel}
              </h2>
              <p className="text-[11px] text-slate-500">via Meta Oficial</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-slate-500 hover:text-slate-200 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          {step === 1 ? (
            <>
              {/* Badge Meta Oficial */}
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-blue-400" />
                <span className="text-[11px] text-blue-400 font-semibold">
                  Conexão segura via Meta for Developers
                </span>
              </div>

              <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                Você será redirecionado para a Meta para autorizar o acesso do{" "}
                <strong className="text-slate-100">Cliver CRM</strong>. Nenhuma senha é compartilhada conosco.
              </p>

              {/* Passos do processo */}
              <div className="mb-5 space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">O que vai acontecer:</p>
                {(steps[provider] || []).map((s, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-[#25D366]/15 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-[#25D366]">{i + 1}</span>
                    </div>
                    <span className="text-xs text-slate-400">{s}</span>
                  </div>
                ))}
              </div>

              {/* Aviso de segurança */}
              <div className="flex items-start gap-2 bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2.5 mb-5">
                <ShieldCheck className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-400 leading-tight">
                  Seus tokens são armazenados com segurança e nunca expostos.
                  Você pode desconectar a qualquer momento.
                </p>
              </div>

              <Button
                onClick={handleConnect}
                disabled={busy}
                className="w-full bg-[#1877F2] hover:bg-[#1565d8] text-white font-semibold h-10 text-sm"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Continuar com Meta
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <Loader2 className="w-10 h-10 animate-spin text-[#1877F2]" />
              <div>
                <p className="text-sm font-semibold text-slate-100 mb-1">Aguardando autorização</p>
                <p className="text-xs text-slate-400">
                  Complete a autorização na janela da Meta que foi aberta.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-xs text-slate-500 hover:text-slate-300 underline"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Advanced settings (collapsible) ──────────────────────────────────────────

function AdvancedSettings({ children, label = "Configuração avançada" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border border-slate-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-800/40 transition"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
          <span className="text-[10px] text-slate-600 font-normal normal-case">
            (admin / desenvolvedor)
          </span>
        </div>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-slate-600" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-slate-600" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-slate-950/40 border-t border-slate-800">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Connected account display ─────────────────────────────────────────────────

function ConnectedAccount({ provider, data }) {
  const isConnected = data?.status === "connected" || data?.status === "connected_mock";
  if (!isConnected) return null;

  const lines = {
    whatsapp: [
      data.phone_number && `Número: ${data.phone_number}`,
      data.waba_name && `WABA: ${data.waba_name}`,
      data.mock_note && `Modo: Desenvolvimento`,
    ].filter(Boolean),
    instagram: [
      data.instagram_username && `@${data.instagram_username}`,
      data.instagram_name && data.instagram_name,
      data.mock_note && `Modo: Desenvolvimento`,
    ].filter(Boolean),
    facebook: [
      data.page_name && `Página: ${data.page_name}`,
      data.page_id && `ID: ${data.page_id}`,
      data.mock_note && `Modo: Desenvolvimento`,
    ].filter(Boolean),
  };

  const info = lines[provider] || [];
  if (!info.length) return null;

  return (
    <div className="mt-3 flex items-start gap-3 bg-[#25D366]/5 border border-[#25D366]/20 rounded-xl px-4 py-3">
      <CheckCircle2 className="w-4 h-4 text-[#25D366] shrink-0 mt-0.5" />
      <div className="space-y-0.5">
        {info.map((line, i) => (
          <p key={i} className="text-xs text-slate-300">{line}</p>
        ))}
        {data.connected_at && (
          <p className="text-[10px] text-slate-500 mt-1">
            Conectado em {new Date(data.connected_at).toLocaleDateString("pt-BR")}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main MetaIntegrationCard ──────────────────────────────────────────────────

/**
 * Card profissional SaaS para integrações Meta (WhatsApp, Instagram, Facebook).
 *
 * Props:
 *   provider        — "whatsapp" | "instagram" | "facebook"
 *   title           — ex: "WhatsApp Oficial Meta"
 *   description     — texto descritivo
 *   icon            — componente Lucide
 *   status          — status atual da integração (do backend meta_oauth)
 *   statusData      — dados completos do status (phone_number, instagram_username, etc.)
 *   onConnect       — async fn chamada ao clicar em Conectar
 *   onDisconnect    — async fn chamada ao clicar em Desconectar
 *   onRefreshStatus — fn para recarregar status
 *   advancedContent — JSX para renderizar dentro da seção Configuração Avançada
 *   webhookUrl      — URL do webhook para exibir na seção avançada
 *   logs            — array de logs do backend
 */
export default function MetaIntegrationCard({
  provider,
  title,
  description,
  icon: Icon,
  statusData = {},
  onConnect,
  onDisconnect,
  onRefreshStatus,
  advancedContent,
  webhookUrl,
  logs = [],
}) {
  const [showModal, setShowModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const status = statusData?.status ?? "disconnected";
  const isConnected = status === "connected" || status === "connected_mock";
  const isConnecting = status === "connecting";

  const connectLabel = {
    whatsapp: "Conectar WhatsApp",
    instagram: "Conectar Instagram",
    facebook: "Conectar Facebook",
  }[provider] || "Conectar";

  const providerLabel = {
    whatsapp: "WhatsApp",
    instagram: "Instagram",
    facebook: "Facebook",
  }[provider] || provider;

  const handleConnect = async () => {
    if (onConnect) {
      await onConnect({ openModal: () => setShowModal(true), closeModal: () => setShowModal(false) });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Desconectar ${providerLabel}? O canal deixará de receber mensagens.`)) return;
    setDisconnecting(true);
    try {
      await onDisconnect?.();
      toast.success(`${providerLabel} desconectado`);
    } catch {
      toast.error(`Erro ao desconectar ${providerLabel}`);
    }
    setDisconnecting(false);
  };

  const copyWebhook = () => {
    if (webhookUrl) {
      navigator.clipboard.writeText(webhookUrl);
      toast.success("URL do webhook copiada!");
    }
  };

  return (
    <>
      <MetaConnectionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        provider={provider}
        providerLabel={providerLabel}
        providerIcon={Icon}
        onConnect={handleConnect}
      />

      <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 transition hover:border-slate-700">
        {/* ─ Header ─ */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            {/* Ícone do canal */}
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
              isConnected ? "bg-[#25D366]/15" : "bg-slate-800"
            }`}>
              <Icon className={`w-5 h-5 ${isConnected ? "text-[#25D366]" : "text-slate-400"}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                  {title}
                </h3>
                {/* Badge Meta Oficial */}
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-400 bg-blue-400/10 border border-blue-400/20 rounded-full px-2 py-0.5">
                  <ShieldCheck className="w-2.5 h-2.5" />
                  Meta Oficial
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
            </div>
          </div>

          {/* Status badge */}
          <div className="shrink-0">
            <StatusBadge status={status} />
          </div>
        </div>

        {/* ─ Connected account info ─ */}
        <ConnectedAccount provider={provider} data={statusData} />

        {/* ─ Action buttons ─ */}
        <div className="flex items-center gap-2 mt-4">
          {isConnected ? (
            <>
              <Button
                onClick={handleDisconnect}
                disabled={disconnecting}
                variant="outline"
                className="flex-1 bg-transparent border-slate-700 text-slate-300 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40 transition text-xs h-9"
              >
                {disconnecting ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Desconectando…</>
                ) : (
                  <><WifiOff className="w-3.5 h-3.5 mr-1.5" /> Desconectar</>
                )}
              </Button>
              <Button
                onClick={() => setShowModal(true)}
                variant="outline"
                className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 transition text-xs h-9"
                title="Reconectar"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Reconectar
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setShowModal(true)}
              disabled={isConnecting}
              className="flex-1 bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold text-xs h-9"
            >
              {isConnecting ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Conectando…</>
              ) : (
                <><Wifi className="w-3.5 h-3.5 mr-1.5" /> {connectLabel}</>
              )}
            </Button>
          )}

          {/* Botão de refresh */}
          <button
            onClick={onRefreshStatus}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition"
            title="Atualizar status"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* ─ Logs resumidos ─ */}
        {logs.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowLogs((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-300 transition mb-2"
            >
              {showLogs ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showLogs ? "Ocultar logs" : `Ver logs de conexão (${logs.length})`}
            </button>
            {showLogs && (
              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3">
                <IntegrationLogs logs={logs} />
              </div>
            )}
          </div>
        )}

        {/* ─ Advanced settings ─ */}
        <AdvancedSettings>
          {/* Webhook URL */}
          {webhookUrl && (
            <div className="mb-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                URL do Webhook
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={webhookUrl}
                  className="flex-1 bg-slate-950 border-slate-800 text-xs text-slate-300 font-mono h-8"
                />
                <button
                  onClick={copyWebhook}
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 rounded-lg transition"
                  title="Copiar URL"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Token mascarado */}
          {statusData?.token_masked && (
            <div className="mb-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                Token (mascarado)
              </Label>
              <Input
                readOnly
                value={statusData.token_masked}
                className="bg-slate-950 border-slate-800 text-xs text-slate-400 font-mono h-8"
              />
            </div>
          )}

          {/* Campos técnicos específicos do provider */}
          {advancedContent && (
            <div className="mt-1">{advancedContent}</div>
          )}

          {/* Logs completos na área avançada */}
          {logs.length > 0 && (
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 block">
                Histórico completo
              </Label>
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
                <IntegrationLogs logs={logs} />
              </div>
            </div>
          )}
        </AdvancedSettings>
      </div>
    </>
  );
}

export { StatusBadge, IntegrationLogs, AdvancedSettings, MetaConnectionModal };
